// src/components/OutputData.jsx
import { useContext, useEffect, useMemo } from 'react'
import { useFirestoreGeneral } from '../../../firebase/useFirestore'
import LiveReportRenderContext from '../../../StateManagment/LiveReportRenderContext'
import useSettings from '../../../hooks/Settings'
import OutputMultiLineGraph from '../../Visualisations/outputs/OutputMultiLineGraph'
import OutputBarchart from '../../Visualisations/outputs/OutputBarchart'
import MetricVisualization from '../../dataVisualizations/MetricVisualization'
import OutputMeta from '../../outputs/OutputMeta'
import CardHeaderInfoPopover from '../../common/CardHeaderInfoPopover'
import ProjectsBreakdownSection from '../../common/ProjectsBreakdownSection'
import { useOutputData } from '../../data/useOutputData'
import useOutputFieldResults from '../../data/useOutputFieldResults'
import summarizeOutputFields from '../../../helpers/dataHelpers/summarizeOutputFields'
import { toDateKey } from '../../../helpers/dataHelpers/toJsDate'
import buildWeeklyProjectCounts from '../../../helpers/dataHelpers/buildWeeklyProjectCounts'
import { BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts'
import { WEEKLY_CHART_COLORS } from '../../../helpers/visualisationDefaults'
import { CHART_MARK_STROKE, CHART_MARK_STROKE_WIDTH } from '../../../helpers/chartPalette'

/**
 * Transform pre-fetched template eventResults into the chart data shape
 * expected by OutputBarchart / OutputMultiLineGraph.
 *
 * @param {object[]} eventResults  Raw EventResults from exportTemplateData
 * @param {string} outputId        The selected output ID
 * @param {object[]} outputsList   Outputs array (for title lookup)
 * @param {'line'|'bar'} graphType
 */
const transformTemplateData = (eventResults, outputId, outputsList, graphType) => {
  const filtered = eventResults.filter(r => r.OutputID === outputId)
  if (!filtered.length) return { data: [], growth: {}, totalValue: 0 }

  const titleMap = Object.fromEntries((outputsList || []).map(o => [o.ID, o.Title]))
  const title = titleMap[outputId] || 'Output'

  const grouped = {}
  let totalValue = 0

  for (const doc of filtered) {
    // Same day key as the live path and the tooltip (toDateKey).
    const dateString = toDateKey(doc.Timestamp)
    if (!dateString) continue

    const value = Number(doc.Value ?? doc.Result ?? 0)

    if (!grouped[dateString]) grouped[dateString] = { name: dateString }
    if (!grouped[dateString][title]) grouped[dateString][title] = { total: 0, projects: {} }
    grouped[dateString][title].total += value
    totalValue += value
  }

  const array = Object.values(grouped)
  array.sort((a, b) => {
    const [dA, mA, yA] = a.name.split('-').map(Number)
    const [dB, mB, yB] = b.name.split('-').map(Number)
    return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB)
  })

  if (graphType === 'line' && array.length > 1) {
    for (let i = 1; i < array.length; i++) {
      const curr = array[i]
      const prev = array[i - 1]
      Object.keys(prev).forEach((key) => {
        if (key === 'name') return
        if (!curr[key]) {
          curr[key] = JSON.parse(JSON.stringify(prev[key]))
        } else {
          curr[key].total = curr[key].total === 0
            ? prev[key].total
            : curr[key].total + prev[key].total
        }
      })
    }
  }

  return {
    data: array,
    growth: { perOutput: {}, perProject: {}, average: 0 },
    totalValue,
  }
}

/**
 * @param {object} props
 * @param {string} [props.field]
 * @param {string} props.outputId
 * @param {'line'|'bar'|'number'} [props.graphType]
 * @param {string} [props.color]
 * @param {string} [props.startDate]
 * @param {string} [props.endDate]
 * @param {string} [props.graphColor]
 * @param {string} [props.lineColor]
 * @param {string} [props.barColor]
 * @param {string|number} [props.height]
 * @param {string|number} [props.width]
 * @param {string} [props.numberFontSize] - CSS font-size for the 'number' graph type (e.g. '48px').
 * @param {'left'|'center'|'right'} [props.numberTextAlign] - Alignment for the 'number' graph type.
 * @param {string} [props.numberFontWeight] - Font weight for the 'number' graph type.
 * @param {object} [props.templateOutputData]  Pre-fetched data for template mode (if not provided, will fetch live)
 * @param {React.ReactNode} [props.dashboardOptions]
 * @param {string|null} [props.personaId]
 * @returns {JSX.Element}
 */
const OutputData = ({
  field,
  outputId,
  graphType = 'line',
  color,
  startDate,
  endDate,
  graphColor,
  lineColor,
  barColor,
  height,
  width,
  numberFontSize,
  numberTextAlign,
  numberFontWeight,
  templateOutputData,
  dashboardOptions,
  personaId = null,
}) => {
  const portfolio = useSettings().portfolio
  const isTemplate = !!templateOutputData
  const inLiveReport = useContext(LiveReportRenderContext)

  // Live mode: Firestore subscription for the Output doc (title lookup, activity name).
  // Template mode: skip Firestore — read outputs from the pre-fetched blob.
  const liveOutputs = useFirestoreGeneral('Outputs', 'ID', isTemplate ? '' : (outputId || ''))
  const outputs = isTemplate ? (templateOutputData?.outputs || []) : liveOutputs

  // Live mode: fetch records + processed data via the Cloud Function hook.
  // Template mode: pass empty outputId so useOutputData short-circuits.
  const liveData = useOutputData({
    outputId: isTemplate ? '' : outputId,
    startDate: isTemplate ? null : startDate,
    endDate: isTemplate ? null : endDate,
    graphType,
    outputsList: isTemplate ? null : liveOutputs,
    personaId,
  })

  useEffect(() => {
    if (!isTemplate) liveData.refresh?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolio])

  // Template mode: derive everything from pre-fetched data.
  const templateResult = useMemo(() => {
    if (!isTemplate || !outputId) return null
    return transformTemplateData(
      templateOutputData?.eventResults || [],
      outputId,
      templateOutputData?.outputs || [],
      graphType,
    )
  }, [isTemplate, outputId, templateOutputData, graphType])

  const data = isTemplate ? (templateResult?.data || []) : liveData.data
  const isLoading = isTemplate ? false : liveData.isLoading
  const growth = isTemplate ? (templateResult?.growth || {}) : liveData.growth
  const projectHierarchy = isTemplate ? {} : liveData.projectHierarchy
  const projectValues = isTemplate ? {} : liveData.projectValues
  const totalValue = isTemplate ? (templateResult?.totalValue || 0) : liveData.totalValue
  const error = isTemplate ? null : liveData.error

  // Activity name for the InfoPopover modal — derive ActivityID from the loaded output doc.
  // Skipped inside a live report: the only consumer is the info popover, which is
  // hidden there, so there's no point opening the Activities subscription.
  const currentOutput = outputs && outputs.find(o => o.ID === outputId)
  const liveActivities = useFirestoreGeneral('Activities', 'ID', (isTemplate || inLiveReport) ? '' : (currentOutput?.ActivityID || ''))
  const templateActivities = isTemplate
    ? (templateOutputData?.activities || []).filter(a => a.ID === currentOutput?.ActivityID)
    : null
  const activities = isTemplate ? templateActivities : liveActivities
  const activityName = (activities && activities[0] && activities[0].Activity) || '—'

  // The activity's other fields, summarized per date for the chart tooltip
  // (updateverzoek Team Sportclub, 2026-10-14). Not for the bare number: it
  // has no tooltip, so nothing is fetched there.
  const fieldResults = useOutputFieldResults({
    activityId: currentOutput?.ActivityID || '',
    startMs: liveData.startMs,
    endMs: liveData.endMs,
    enabled: graphType !== 'number',
    templateOutputData: isTemplate ? templateOutputData : null,
  })
  const fieldSummaries = useMemo(() => ({
    status: fieldResults.status,
    byDate: summarizeOutputFields({
      outputRecords: isTemplate
        ? (templateOutputData?.eventResults || []).filter(r => r.OutputID === outputId)
        : (liveData.records || []),
      fieldResults: fieldResults.fieldResults,
    }),
  }), [fieldResults, isTemplate, templateOutputData, outputId, liveData.records])
  const outputTitle = currentOutput?.Title || 'Resultaten'

  // Weekly per-child result counts for the details-modal stacked bar chart:
  // when results came in, and from which child project. Hierarchy-aware so it
  // includes synced child projects, not just the tenant's own records.
  const weeklyData = useMemo(() => {
    const source = isTemplate
      ? (templateOutputData?.eventResults || []).filter(r => r.OutputID === outputId)
      : (liveData.records || [])
    return buildWeeklyProjectCounts(source)
  }, [isTemplate, templateOutputData, liveData.records, outputId])

  return (
    <>
      {!inLiveReport && (
      <div className='home-kpi-question-title-container'>
        {!isTemplate && <p><OutputMeta id={outputId} /></p>}
        {isTemplate && outputs.length > 0 && (
          <p>{outputs.find(o => o.ID === outputId)?.Title || ''}</p>
        )}
        <CardHeaderInfoPopover
          label="Details"
          content={(
            <div>
              <div className="info-popover-row">
                <span className="info-popover-row-label">Activiteit</span>
                <span className="info-popover-row-value">{activityName}</span>
              </div>
              <div className="info-popover-row">
                <span className="info-popover-row-label">{outputTitle}</span>
                <span className="info-popover-row-value">{totalValue ?? 0}</span>
              </div>
              {weeklyData.rows.length > 0 && (
                <div className="info-popover-chart">
                  <p className="info-popover-chart-title">Resultaten per week</p>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={weeklyData.rows} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <XAxis dataKey="name" axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} axisLine={false} tickLine={false} />
                      <RechartsTooltip />
                      <Legend />
                      {weeklyData.projects.map((project, i) => (
                        <Bar
                          key={project}
                          dataKey={project}
                          stackId="results"
                          fill={WEEKLY_CHART_COLORS[i % WEEKLY_CHART_COLORS.length]}
                        stroke={CHART_MARK_STROKE} strokeWidth={CHART_MARK_STROKE_WIDTH} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
              <ProjectsBreakdownSection values={projectValues} valueLabel={outputTitle} />
            </div>
          )}
        />
      </div>
      )}
      {/* Mislukte lading is GEEN lege toestand: laat de grafiek dan helemaal
          weg, anders zet de lege-toestandtekst er 'nog geen resultaten' onder
          en leest de gebruiker een bewering die niet waar is.
          AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading */}
      {error ? (
        <p className='graph-data-error'>Er ging iets mis bij het laden van de data.</p>
      ) : graphType === 'number' ? (
        <MetricVisualization
          value={totalValue}
          isLoading={isLoading}
          align={numberTextAlign}
          valueStyle={{ color: color, fontSize: numberFontSize, fontWeight: numberFontWeight }}
        />
      ) : data.length === 1 ? (
        <OutputBarchart
          data={data}
          isLoading={isLoading}
          outputs={outputs}
          graphType={graphType}
          growth={growth}
          portfolio={portfolio}
          color={color}
          graphColor={graphColor}
          barColor={color || barColor}
          projectHierarchy={projectHierarchy}
          fieldSummaries={fieldSummaries}
          width={width || '100%'}
          height={height || 400}
        />
      ) : (
        <OutputMultiLineGraph
          data={data}
          isLoading={isLoading}
          outputs={outputs}
          graphType={graphType}
          growth={growth}
          portfolio={portfolio}
          color={color}
          graphColor={graphColor}
          barColor={barColor}
          lineColor={lineColor}
          projectHierarchy={projectHierarchy}
          fieldSummaries={fieldSummaries}
          width={width || '100%'}
          height={height || 400}
        />
      )}
      {dashboardOptions}
    </>
  )
}

export default OutputData
